import type { BookLibrary } from "../models/types";
import * as onedrive from "./onedrive";
import { loadLocalLibrary, saveLocalLibrary } from "./local-storage";

// ---------------------------------------------------------------------------
// Test mode — when ?testMode is in the URL, bypass OneDrive auth and use
// localStorage for persistence so e2e tests can run without real credentials.
// ---------------------------------------------------------------------------

const TEST_MODE_PARAM = "testMode";
const TEST_MODE_SIGNED_IN_KEY = "booklist_test_signed_in";

// Cache testMode at module load so pushState URL changes can't flip it mid-session
const _isTestMode = new URLSearchParams(window.location.search).has(
  TEST_MODE_PARAM,
);

export function isTestMode(): boolean {
  return _isTestMode;
}

function isTestModeSignedIn(): boolean {
  return (
    isTestMode() &&
    localStorage.getItem(TEST_MODE_SIGNED_IN_KEY) === "true"
  );
}

// ---------------------------------------------------------------------------

export async function load(): Promise<BookLibrary> {
  if (isTestMode()) {
    return loadLocalLibrary();
  }
  if (!onedrive.isSignedIn()) {
    throw new Error("Not signed in to OneDrive");
  }
  return onedrive.loadLibrary();
}

export async function save(library: BookLibrary): Promise<void> {
  if (isTestMode()) {
    // Test hook: allow E2E tests to force save failures
    if (localStorage.getItem("booklist_test_force_save_error") === "true") {
      throw new Error("Simulated save failure for testing");
    }
    saveLocalLibrary(library);
    return;
  }
  if (!onedrive.isSignedIn()) {
    throw new Error("Not signed in to OneDrive");
  }
  await onedrive.saveLibrary(library);
}

export async function signIn(): Promise<void> {
  if (isTestMode()) {
    localStorage.setItem(TEST_MODE_SIGNED_IN_KEY, "true");
    window.location.reload();
    return;
  }
  // This performs a full-page redirect to Microsoft login.
  // The page will navigate away; code after this never runs.
  await onedrive.signIn();
}

/**
 * Call on every page load before initialising the app.
 * If the URL contains an auth code, exchanges it for a token.
 * Returns true if sign-in completed.
 */
export async function handleRedirectIfNeeded(): Promise<boolean> {
  if (isTestMode()) return false;
  return onedrive.handleRedirectIfNeeded();
}

export function signOut(): void {
  if (isTestMode()) {
    localStorage.removeItem(TEST_MODE_SIGNED_IN_KEY);
    return;
  }
  onedrive.signOut();
}

export function isSignedIn(): boolean {
  if (isTestMode()) return isTestModeSignedIn();
  return onedrive.isSignedIn();
}

export function hasRefreshToken(): boolean {
  if (isTestMode()) return false;
  return onedrive.hasRefreshToken();
}

export async function trySilentRefresh(): Promise<boolean> {
  if (isTestMode()) return false;
  return onedrive.trySilentRefresh();
}

export function isConfigured(): boolean {
  if (isTestMode()) return true;
  return onedrive.isConfigured();
}

export { exportLibraryToFile, importLibraryFromFile } from "./local-storage";
